import React, { useState } from 'react';
import { MemberDocument } from '../../graphql/__generated__/graphql';
import { useDocuments } from '../../hooks/useDocuments';
import { useNotificationContext } from '../../contexts/NotificationContext';
import PreviewModal from './DocumentPreviewModal';
import './index.scss';

export interface IDocumentItemProps {
    document: MemberDocument;
    compact?: boolean;
}

export const DocumentItem: React.FC<IDocumentItemProps> = ({ document, compact }) => {
    const { operations } = useDocuments();
    const { error: notifyError } = useNotificationContext();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();
    const [previewDocId, setPreviewDocId] = useState<string | undefined>();
    const [previewDocName, setPreviewDocName] = useState<string | undefined>();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | undefined>();

    const deleteDocument = async () => {
        if (
            window.confirm(
                `Are you sure you want to delete document '${document.name}'?\n\nThis action can't be undone.`
            )
        ) {
            try {
                await operations.deleteDocument(document.id);
            } catch (error) {
                notifyError(
                    error instanceof Error
                        ? error.message
                        : 'The document could not be deleted. Please try again.'
                );
            }
        }
    };

    const formatDateUK = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const expiryDate = new Date(document.expiries);
    const isExpired = expiryDate <= new Date();

    const openPreview = async () => {
        //if (!isPdf(document)) {
        //    setPreviewError('Preview is available for PDF files only.');
        //    setPreviewOpen(true);
        //    return;
        //}

        setPreviewDocId(document.id);
        setPreviewDocName(document.name || 'Document');
        setPreviewLoading(true);
        setPreviewError(undefined);
        setPreviewOpen(true);

        try {
            const blob: Blob = await operations.getDocumentBlob(document.id);
            const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            setPreviewUrl((old) => {
                if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
                return url;
            });
        } catch {
            setPreviewError('Sorry, there was a problem loading the preview.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const closePreview = () => {
        if (window.document.fullscreenElement) window.document.exitFullscreen().catch(() => {});
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(undefined);
        setPreviewDocId(undefined);
        setPreviewDocName(undefined);
        setPreviewError(undefined);
        setPreviewOpen(false);
    };

    return (
        <>
            <tr key={document.id}>
                <td>
                    <span className={isExpired && document.expiries ? 'text-danger' : undefined}>
                        {isExpired && document.expiries && (
                            <i
                                style={{ marginRight: '1em' }}
                                className="bi bi-exclamation-circle-fill"
                            ></i>
                        )}
                        {document.name}
                    </span>
                </td>

                {!compact && (
                    <>
                        <td>{document.expiries ? formatDateUK(expiryDate) : 'N/A'}</td>
                        <td>{document.cpd !== 0 ? document.cpd : 'N/A'}</td>
                    </>
                )}

                <td align="right">
                    <div className="icon">
                        <a
                            href="#"
                            title="Preview Document"
                            onClick={(e) => {
                                e.preventDefault();
                                openPreview();
                            }}
                        >
                            <i className="bi bi-eye"></i>
                        </a>
                    </div>
                    <div className="icon">
                        <a
                            href="#"
                            onClick={() => operations.downloadDocument(document.id)}
                            title="Download Document"
                        >
                            <i className="bi bi-cloud-arrow-down"></i>
                        </a>
                    </div>
                    <div className="icon">
                        <a
                            href="#"
                            onClick={() => operations.shareDocument(document.id)}
                            title="Share Document"
                        >
                            <i className="bi bi-share"></i>
                        </a>
                    </div>
                    <div className="icon">
                        <a onClick={deleteDocument} href="#" title="Delete Document">
                            <i className="bi bi-trash3"></i>
                        </a>
                    </div>
                </td>
            </tr>

            <PreviewModal
                open={previewOpen}
                title={previewDocName || 'Preview'}
                url={previewUrl}
                loading={previewLoading}
                error={previewError}
                onDownload={() => previewDocId && operations.downloadDocument(previewDocId)}
                onClose={closePreview}
            />
        </>
    );
};
